import React, {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDebouncedCallback } from "common-client/utils/useDebouncedCallback";
import {
  OmniboxSearchResult,
  useExecuteSearchLazyQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn } from "../Common/__styles__/Layout";
import Divider from "../Common/Divider";
import { Icon } from "../Common/Icons/LucideIcons";
import { Text } from "../Inputs";
import { InternalMapContext } from "../Maps/InternalMapContextProvider";
import { LayerContext } from "../Maps/layers";
import {
  LeftContentWrapper,
  OmniboxSearchContainer,
  OmniboxSearchInputWrapper,
} from "./__styles__/OmniboxSearch";
import { OmniboxResults } from "./OmniboxResults";
import { SearchContext } from "./SearchContext";

const OmniboxSearchInput = () => {
  const { isSearching, setIsSearching } = useContext(LayerContext);
  const { setResults, query, setQuery, setLoadingSearch, resetSearch } =
    useContext(SearchContext);

  const [executeSearch] = useExecuteSearchLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: data => {
      setResults(data?.search || []);
      setLoadingSearch(false);
    },
  });

  const debouncedExecuteSearch = useDebouncedCallback(async (value: string) => {
    await executeSearch({ variables: { query: value } });
  }, 250);

  return (
    <OmniboxSearchInputWrapper>
      <LeftContentWrapper>
        <Icon iconName="search" color="contentSecondary" size="16" />
        <Text
          name="omnibox-search-input"
          data-testid="omnibox-search-input"
          placeholder="Search..."
          onChange={async value => {
            setIsSearching(!!value);
            setLoadingSearch(!!value);
            setQuery(value);
            if (value) {
              await debouncedExecuteSearch(value);
            }
          }}
          onFocus={() => setIsSearching(true)}
          size="small"
          value={query}
          maxLength={256}
          style={{
            padding: 0,
            border: "none",
            height: "24px",
          }}
        />
      </LeftContentWrapper>
      {(query || isSearching) && (
        <Icon
          iconName="x"
          color="contentSecondary"
          size="16"
          data-testid="omnibox-search-clear"
          style={{ cursor: "pointer" }}
          onClick={() => {
            resetSearch();
          }}
        />
      )}
    </OmniboxSearchInputWrapper>
  );
};

export const OmniboxSearch = ({
  handleResultClick,
  halfMap,
}: {
  halfMap: boolean;
  handleResultClick: ComponentProps<typeof OmniboxResults>["onResultClick"];
}) => {
  const [results, setResults] = useState<OmniboxSearchResult[]>([]);
  const { isSearching, setIsSearching } = useContext(LayerContext);
  const [query, setQuery] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const resetSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setIsSearching(false);
    setLoadingSearch(false);
  }, [setResults, setIsSearching, setQuery]);

  // needed while we still have the old search around
  const { setResetSearchCategory } = useContext(InternalMapContext);
  useEffect(() => {
    if (setResetSearchCategory) {
      setResetSearchCategory(() => {
        resetSearch();
      });
    }
  }, [setResetSearchCategory, resetSearch]);

  const onResultClick = useCallback(
    (
      result: Parameters<
        ComponentProps<typeof OmniboxResults>["onResultClick"]
      >[0],
    ) => {
      resetSearch();
      handleResultClick(result);
    },
    [handleResultClick, resetSearch],
  );
  return (
    <SearchContext.Provider
      value={{
        isSearching,
        setIsSearching,
        results,
        setResults,
        query,
        setQuery,
        loadingSearch,
        setLoadingSearch,
        resetSearch,
      }}
    >
      <OmniboxSearchContainer>
        <FlexColumn>
          <OmniboxSearchInput />
          {isSearching &&
            (halfMap ?
              <Divider margins={`0 -${spacing.s.value}`} />
            : <Divider extension={16} />)}
        </FlexColumn>
        {isSearching && <OmniboxResults onResultClick={onResultClick} />}
      </OmniboxSearchContainer>
    </SearchContext.Provider>
  );
};
